import { type CSSProperties, type ReactNode, useState } from "react";
import { formatCurrency } from "@/common/utils/currencyUtils";
import TransitionGroup from "react-transition-group/TransitionGroup";
import CSSTransition from "react-transition-group/CSSTransition";
import { createNodeRefs } from "@/common/nodeRefs";
import {
  FAVORITE_ANNOTATION,
  FAVORITE_CTA,
  FAVORITE_CTA_LABEL,
  FAVORITE_DESCRIPTION,
  FAVORITE_DETAILS,
  FAVORITE_DETAILS_FOOTER,
  FAVORITE_DETAILS_FOOTER_CONTROLS,
  FAVORITE_DETAILS_SCROLLER,
  FAVORITE_FOOTNOTE,
} from "@/components/favorites/favoritesClasses";
import { eventNames } from "@/modules/Constants";
import LinkData, { linkTypes } from "@/plugin/LinkData";
import * as labelUtils from "@/plugin/labelUtils";
import { clusterNames } from "@/plugin/ProductPlugin";
import type { ExperienceState } from "@/types/state";

import TrashIcon from "@svg/trash.svg";
import { sanitizeHtml } from "@/modules/sanitizeHtml";

const REGION = "Favorite Details";

interface ChoiceOption {
  name?: string;
  value?: string;
}

interface Choice {
  type: string;
  name: string;
  options: ChoiceOption[];
}

interface FavoriteArticle {
  title: string;
  subtitle?: string;
  annotation?: string;
  footnote?: string;
  description: string;
  price?: number;
  currency?: string;
  titleImage?: { filename?: string };
  choices?: Choice[];
}

type FavoriteDetailsProps = {
  experienceState: ExperienceState;
  onApplicationEvent: (event: Record<string, unknown>) => void;
  onRemoveFavoriteAtIndex: (region: string, index: number) => void;
};

const CHOICE_SEPARATOR =
  "separator landscape tw:h-[1px] tw:w-[95%] tw:bg-[rgba(200,200,200,0.5)]";
const OPTION_NAME =
  "option-name tw:table-cell tw:pt-[6px] tw:pb-[6px] tw:pl-[10px] tw:whitespace-nowrap";
const OPTION_VALUES = "tw:table-cell tw:pt-[6px] tw:pb-[6px]";
const OPTION_VALUE = "tw:ml-[5px] tw:inline-block";

function addListChoice(choiceNum: number, list: ReactNode[], choice: Choice) {
  const values = [];

  for (let n = 0; n < choice.options.length; n++) {
    values.push(
      <li key={`${list.length}-${n}`} className={OPTION_VALUE}>
        {choice.options[n].name}
      </li>,
    );
  }

  list.push(
    <div key={`option_${choiceNum}`} className="option list tw:table-row">
      <span className={OPTION_NAME}>{choice.name}</span>
      <ul className={OPTION_VALUES}>{values}</ul>
    </div>,
  );
}

function addColorChoice(choiceNum: number, list: ReactNode[], choice: Choice) {
  const values = [];

  for (let n = 0; n < choice.options.length; n++) {
    const style: CSSProperties = {
      backgroundColor: choice.options[n].value,
    };

    values.push(
      <li
        key={`${list.length}-${n}`}
        className={`${OPTION_VALUE} tw:h-[12px] tw:w-[12px] tw:[transform:translateY(1px)]`}
        style={style}
      ></li>,
    );
  }

  list.push(
    <div key={`option_${choiceNum}`} className="option color tw:table-row">
      <span className={OPTION_NAME}>{choice.name}</span>
      <ul className={OPTION_VALUES}>{values}</ul>
    </div>,
  );
}

function buildChoices(choices: Choice[] | undefined) {
  if (!choices || choices.length === 0) {
    return <div className="favorite-choices tw:mt-[10px]" />;
  }

  const htmlChoices: ReactNode[] = [];

  for (let n = 0; n < choices.length; n++) {
    const choice = choices[n];

    if (n > 0) {
      htmlChoices.push(<div key={`${n}`} className={CHOICE_SEPARATOR} />);
    }

    switch (choice.type) {
      case "list":
        addListChoice(n, htmlChoices, choice);
        break;
      case "color":
        addColorChoice(n, htmlChoices, choice);
        break;
      default:
        console.warn(`Unexpected option choice for experience: ${choice.type}`);
    }
  }

  return (
    <div className="favorite-choices tw:mt-[10px]">
      <div className={CHOICE_SEPARATOR} />
      {htmlChoices}
      <div className={CHOICE_SEPARATOR} />
    </div>
  );
}

export default function FavoriteDetails({
  experienceState,
  onApplicationEvent,
  onRemoveFavoriteAtIndex,
}: FavoriteDetailsProps) {
  const [nodeRefFor] = useState(() => createNodeRefs<HTMLDivElement>());

  const experienceData = experienceState.experienceData;
  const applicationData = experienceState.applicationData;
  const metadata = experienceData.metadata;
  const favoritesData = experienceState.favoritesData;
  const article = favoritesData.getFavoriteArticle() as FavoriteArticle | null;

  if (!article) {
    return <div className={FAVORITE_DETAILS}></div>;
  }

  const displayPrice = () =>
    article.price ? formatCurrency(article.currency, article.price) : "";

  const annotation = (
    <span className={FAVORITE_ANNOTATION}>{article.annotation || " "}</span>
  );
  const footnote = (
    <span className={FAVORITE_FOOTNOTE}>{article.footnote || " "}</span>
  );

  const descriptions = applicationData.hasFavoriteDetailsDescription() ? (
    <div
      className={FAVORITE_DESCRIPTION}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.description) }}
    />
  ) : null;

  const title =
    article.titleImage && article.titleImage.filename ? (
      <img
        className="favorite-title tw:mt-[15px]"
        src={metadata.getAssetUrl(article.titleImage.filename)}
        alt={article.title}
      />
    ) : (
      <h1 className="favorite-title header-1 tw:mt-[15px]">{article.title}</h1>
    );

  const price = article.price ? (
    <span className="price">{displayPrice()}</span>
  ) : (
    ""
  );

  const label = labelUtils.getClusterClickLabel(
    clusterNames.ROOT,
    experienceData.style,
  );

  const nodeRef = nodeRefFor(`details-${favoritesData.favoriteIndex}`);

  return (
    <TransitionGroup component="div" className={FAVORITE_DETAILS}>
      <CSSTransition
        key={`details-${favoritesData.favoriteIndex}`}
        nodeRef={nodeRef}
        classNames="favorite-details-wrapper"
        timeout={200}
      >
        <div
          className="favorite-details-wrapper tw:absolute tw:flex tw:h-full tw:w-full tw:flex-col tw:items-stretch"
          ref={nodeRef}
        >
          <div className={FAVORITE_DETAILS_SCROLLER}>
            {title}
            <h2 className="favorite-subtitle header-2 tw:mt-[5px]">
              {article.subtitle} {price}
            </h2>
            {descriptions}
            {buildChoices(article.choices)}
          </div>
          <div className={FAVORITE_DETAILS_FOOTER}>
            <div className={FAVORITE_DETAILS_FOOTER_CONTROLS}>
              {annotation}
              <div className="favorite-footer-controls tw:relative tw:flex tw:flex-col">
                <button
                  type="button"
                  className={FAVORITE_CTA}
                  onClick={() =>
                    onApplicationEvent({
                      region: REGION,
                      name: eventNames.LINK,
                      clusterName: "fav",
                      cindex: 0,
                      index: favoritesData.favoriteIndex,
                      link: new LinkData(
                        linkTypes.FAVORITE,
                        undefined,
                        "favorite.cta",
                        true,
                      ),
                    })
                  }
                >
                  <span className={FAVORITE_CTA_LABEL}>{label.title}</span>
                </button>
                <button
                  className="favorite-delete button type-3 hover square tw:absolute tw:top-0 tw:right-[5px]"
                  aria-label="Remove from favorites"
                  onClick={() =>
                    onRemoveFavoriteAtIndex(REGION, favoritesData.favoriteIndex)
                  }
                >
                  <TrashIcon className="centered icon-trash-d" />
                </button>
              </div>
              {footnote}
            </div>
          </div>
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
}
